<template>
  <div class="tool-page">
    <div class="page-body">
      <!-- 面包屑 -->
      <div class="breadcrumb">
        <router-link to="/">{{ $t('view.common.home') }}</router-link>
        <template v-if="tool.categoryName">
          <span class="bc-sep">/</span>
          <router-link v-if="tool.categorySlug" :to="'/category/' + tool.categorySlug">{{ tool.categoryName }}</router-link>
          <span v-else>{{ tool.categoryName }}</span>
        </template>
        <span class="bc-sep">/</span>
        <span class="current">{{ tool.name }}</span>
      </div>

      <!-- 工具头部 -->
      <div class="tool-header">
        <div class="header-left">
          <div class="header-icon" :style="{ background: headerBg }">
            <i class="el-icon-s-tools" />
          </div>
          <div class="header-info">
            <h1 class="tool-name">{{ tool.name }}</h1>
            <p class="tool-desc" v-if="tool.description">{{ tool.description }}</p>
            <div class="tool-meta">
              <span class="meta-stat" v-if="tool.useCount"><i class="el-icon-view" /> {{ $t('view.detail.useCount', { count: fmt(tool.useCount) }) }}</span>
              <span class="meta-stat" v-if="tool.favCount"><i class="el-icon-star-on" /> {{ $t('view.detail.favCount', { count: tool.favCount }) }}</span>
              <span class="tool-tags" v-if="tool.tags && tool.tags.length">
                <span v-for="(tag, idx) in tool.tags" :key="idx" class="detail-tag">{{ tag }}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="header-right">
          <el-button
            :class="['fav-btn', { 'is-fav': isFav }]"
            size="small" round
            :icon="isFav ? 'el-icon-star-on' : 'el-icon-star-off'"
            @click="toggleFav"
          >{{ isFav ? $t('view.detail.faved') : $t('view.detail.fav') }}</el-button>
        </div>
      </div>

      <!-- 工具主体 -->
      <div class="tool-component-area" v-if="tool.componentType">
        <ToolLoader :tool="tool" />
      </div>

      <!-- 推荐工具 -->
      <div class="ad-section" v-if="ads.length">
        <div class="ad-grid">
          <AdCard v-for="ad in ads" :key="ad.id" :ad="ad" :tool="tool" />
        </div>
      </div>

      <!-- 使用说明 (SEO-friendly: visible by default) -->
      <div class="tool-info" v-if="tool.detailDesc">
        <div class="info-title"><i class="el-icon-document" /> {{ $t('view.detail.usage') }}</div>
        <div class="detail-content" v-html="renderedDesc" />
      </div>
    </div>
  </div>
</template>

<script>
import ToolLoader from '@/components/tool-components/ToolLoader'
import AdCard from '@/components/tool-components/AdCard'
import { getToolDetail, recordUsage, getActiveAds } from '@/api/site'

const headerColors = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #0ba360, #3cba92)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4e7cff, #6b3cf0)',
  'linear-gradient(135deg, #fa709a, #fee140)',
]

function setMeta(name, content) {
  if (!content) return
  var el = document.querySelector('meta[name="' + name + '"]')
  if (!el) {
    el = document.createElement('meta'); el.name = name
    document.head.appendChild(el)
  }
  el.content = content
}

export default {
  components: { ToolLoader, AdCard },
  created() { this.loadData() },
  data: () => ({ tool: {}, ads: [] }),
  computed: {
    isFav() { return this.tool.id && this.$store.getters.isFav(this.tool.id) },
    headerBg() { return headerColors[(this.tool.id || 0) % headerColors.length] },
    renderedDesc() {
      return this.tool.detailDesc ? this.tool.detailDesc.replace(/\n/g, '<br>') : ''
    }
  },
  methods: {
    fmt(n) { if (!n) return 0; if (n >= 10000) return (n/10000).toFixed(1)+'w'; if (n >= 1000) return (n/1000).toFixed(1)+'k'; return n },
    loadData() {
      getToolDetail(this.$route.params.slug).then(res => {
        this.tool = res.data || {}
        if (this.tool.id) recordUsage(this.tool.id).catch(() => {})

        // SEO: dynamic title & meta
        if (this.tool.seoTitle) {
          document.title = this.tool.seoTitle
        } else if (this.tool.name) {
          document.title = this.tool.name + this.$t('view.detail.docTitleSuffix')
        }
        var desc = this.tool.seoDesc || this.tool.description || ''
        if (desc) setMeta('description', desc)
        var kw = this.tool.seoKeywords || ''
        if (kw) setMeta('keywords', kw)
      })
      getActiveAds('detail').then(res => {
        this.ads = res.data || []
      }).catch(() => { this.ads = [] })
    },
    toggleFav() {
      const isFav = this.$store.getters.isFav(this.tool.id)
      if (isFav) {
        this.tool.favCount = Math.max((this.tool.favCount || 0) - 1, 0)
      } else {
        this.tool.favCount = (this.tool.favCount || 0) + 1
      }
      this.$store.dispatch('toggleFavAction', this.tool)
    }
  },
  watch: { '$route.params.slug'() { this.loadData() } }
}
</script>

<style scoped>
.tool-page { background: #f0f2f8; min-height: 100vh; }
.page-body { max-width: 980px; margin: 0 auto; padding: 28px 20px 80px; }

/* ---- Breadcrumb ---- */
.breadcrumb {
  font-size: 13px; color: #8892a6; margin-bottom: 20px;
  display: flex; align-items: center; gap: 6px;
}
.breadcrumb a { color: #4e7cff; }
.breadcrumb a:hover { color: #3b5ccc; }
.bc-sep { color: #c0c4cc; }
.current { color: #1a1f36; font-weight: 600; }

/* ---- Header ---- */
.tool-header {
  background: #fff; border-radius: 16px; padding: 28px 32px;
  margin-bottom: 24px; border: 1px solid #e8eaef;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
}
.header-left { display: flex; gap: 20px; align-items: flex-start; flex: 1; min-width: 0; }
.header-icon {
  width: 52px; height: 52px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; box-shadow: 0 4px 16px rgba(78,124,255,0.2);
}
.header-icon i { font-size: 24px; color: #fff; }
.header-info { flex: 1; min-width: 0; }
.tool-name { font-size: 24px; font-weight: 700; color: #1a1f36; margin: 0 0 6px; }
.tool-desc { font-size: 14px; color: #606266; margin: 0 0 10px; }

.tool-meta { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.meta-stat { font-size: 13px; color: #8892a6; display: flex; align-items: center; gap: 4px; }
.tool-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.detail-tag {
  font-size: 11px; padding: 2px 10px; border-radius: 4px;
  font-weight: 500; color: #4e7cff; background: #f0f4ff;
}

.header-right { flex-shrink: 0; }
.fav-btn {
  border: 1px solid #e4e7ed !important;
  color: #606266 !important; transition: all 0.2s;
}
.fav-btn:hover { border-color: #4e7cff !important; color: #4e7cff !important; }
.fav-btn.is-fav {
  border-color: #fbbf24 !important;
  background: rgba(251,191,36,0.08) !important;
  color: #d4a017 !important;
}

/* ---- Component Area ---- */
.tool-component-area {
  background: #fff; border-radius: 16px; padding: 28px;
  border: 1px solid #e8eaef; margin-bottom: 20px;
}

/* ---- Ads ---- */
.ad-section { margin-bottom: 20px; }
.ad-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
@media (max-width: 640px) {
  .ad-grid { grid-template-columns: 1fr; }
}

/* ---- Detail Desc ---- */
.tool-info {
  background: #fff; border-radius: 12px; padding: 20px 24px;
  border: 1px solid #e8eaef; margin-bottom: 20px;
}
.info-title {
  font-size: 15px; font-weight: 600; color: #1a1f36;
  margin-bottom: 12px; padding-bottom: 10px;
  border-bottom: 1px solid #f0f2f5;
  display: flex; align-items: center; gap: 8px;
}
.detail-content { font-size: 14px; line-height: 2; color: #4a5568; }

/* ---- Responsive ---- */
@media (max-width: 640px) {
  .tool-header { flex-direction: column; padding: 20px; }
  .tool-component-area { padding: 16px; }
}
</style>
